/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography, Icon, IconName } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export type FeaturesSectionProps = {
  backgroundColor:
    | typeof COLOR_KEYS.blue10
    | typeof COLOR_KEYS.cream
    | typeof COLOR_KEYS.neutral100
    | typeof COLOR_KEYS.neutral200;
  features: {
    description: string;
    heading: string;
    iconName: IconName;
  }[];
  description: RichTextHTMLString;
  heading: string;
};

const Styles = labelStyles('FeaturesSection', {
  root: (backgroundColor: FeaturesSectionProps['backgroundColor']) =>
    css({
      padding: '60px 20px',
      backgroundColor: Colors[backgroundColor],
    }),

  content: css({
    maxWidth: '1170px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '40px',
    textAlign: 'center',

    [MEDIA_MIN_WIDTH.small]: {
      gap: '75px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      gridTemplateColumns: '1fr 1fr',
      gap: '30px',
      textAlign: 'left',
    },
  }),

  sectionHeading: css({
    ...Typography.pageHeader5,
    color: Colors.earth,
    marginBottom: '18px',

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '300px',
    },
  }),

  sectionDescription: css({
    ...Typography.paragraph1Short,
    color: Colors.neutral700,

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '360px',
    },
  }),

  features: css({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '60px',

    [MEDIA_MIN_WIDTH.small]: {
      gridTemplateColumns: '1fr 1fr',
      gap: '112px 30px',
    },
  }),

  featureHeading: css({
    ...Typography.pageHeader9,
    color: Colors.neutral800,
    margin: '12px 0 8px',
  }),

  featureDescription: css({
    ...Typography.paragraph1Short,
    color: Colors.neutral800,
  }),
});

export function FeaturesSection({
  backgroundColor,
  description,
  features,
  heading,
}: FeaturesSectionProps) {
  return (
    <div css={Styles.root(backgroundColor)}>
      <div css={Styles.content}>
        <div>
          <h2 css={Styles.sectionHeading}>{heading}</h2>
          <div css={Styles.sectionDescription}>
            <RichTextHtml html={description} />
          </div>
        </div>
        <div css={Styles.features}>
          {features.map((feature, index) => (
            <div key={`${feature}-${index}`}>
              <Icon color="zapier" name={feature.iconName} size={48} />
              <h3 css={Styles.featureHeading}>{feature.heading}</h3>
              <div css={Styles.featureDescription}>
                <RichTextHtml html={feature.description} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
